import { SparklesIcon } from "@heroicons/react/24/outline";
import Tooltip from "../../shared/Tooltip";
import PriceFormat from "../../../utils/priceFormat";
import classNames from "../../../utils/classNames";

export default function UnlistedIntelligencePriceControls({
  intelligencePrices,
  intelligenceLoading,
  currentDraftPrice,
  onRunLookup,
  onApplyAverage,
  onAdjustPercent,
  onSliderChange,
  onOpenDetails,
}: {
  intelligencePrices: { min: number; max: number; avg: number } | null;
  intelligenceLoading: boolean;
  currentDraftPrice: number | null;
  onRunLookup: () => void;
  onApplyAverage: () => void;
  onAdjustPercent: (percentage: number) => void;
  onSliderChange: (price: number) => void;
  onOpenDetails: () => void;
}) {
  const intelligenceSliderValue =
    intelligencePrices == null
      ? 0
      : Math.min(
        intelligencePrices.max,
        Math.max(intelligencePrices.min, currentDraftPrice ?? intelligencePrices.avg)
      );

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-2">
      {intelligencePrices == null ? (
        <div className="flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={onRunLookup}
            className={classNames(
              "flex-1 rounded-md bg-amber-100 px-2 py-1.5 text-xs font-semibold text-amber-900 transition hover:bg-amber-200",
              intelligenceLoading && "animate-pulse"
            )}
          >
            {intelligenceLoading ? "Loading intelligence..." : "Run Intelligence"}
          </button>
          <IntelligenceDetailsButton onClick={onOpenDetails} />
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onAdjustPercent(-5)}
              className="rounded-md border border-amber-300 px-2 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
            >
              -5%
            </button>
            <button
              type="button"
              onClick={onApplyAverage}
              className="flex-1 rounded-md bg-amber-200 px-2 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-300"
            >
              Avg {PriceFormat(intelligencePrices.avg)}
            </button>
            <button
              type="button"
              onClick={() => onAdjustPercent(5)}
              className="rounded-md border border-amber-300 px-2 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100"
            >
              +5%
            </button>
            <IntelligenceDetailsButton onClick={onOpenDetails} />
          </div>
          <div className="flex items-center gap-2">
            <span className="w-12 text-[10px] font-semibold text-amber-900">
              {PriceFormat(intelligencePrices.min)}
            </span>
            <input
              type="range"
              min={intelligencePrices.min}
              max={intelligencePrices.max}
              step={0.01}
              value={intelligenceSliderValue}
              onChange={(e) => onSliderChange(Number(e.target.value))}
              className="h-2 w-full cursor-pointer accent-amber-500"
            />
            <span className="w-12 text-right text-[10px] font-semibold text-amber-900">
              {PriceFormat(intelligencePrices.max)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function IntelligenceDetailsButton({ onClick }: { onClick: () => void }) {
  return (
    <Tooltip content="Open Wilma Intelligence details" className="shrink-0">
      <button
        type="button"
        onClick={onClick}
        title="Open Wilma Intelligence details"
        className={classNames(
          "group relative min-h-[2.75rem] w-10 overflow-hidden rounded-lg ring-1 ring-inset ring-amber-300/60 shadow-sm transition focus:outline-none focus:ring-2 focus:ring-primary-500/30 hover:brightness-110",
          "bg-gradient-to-br from-amber-300 via-pink-300 to-violet-400"
        )}
      >
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/50 to-transparent skew-x-12 transition-transform duration-700 group-hover:translate-x-full"
        />
        <span className="relative flex items-center justify-center gap-1 text-white drop-shadow-sm">
          <SparklesIcon className="h-4 w-4" aria-hidden="true" />
        </span>
      </button>
    </Tooltip>
  );
}
